import { BusinessNotificationSettingsInput } from '../../../../../service/business/BusinessNotificationSettingsInput';
import { templatesFor } from './notificationTemplates';

/** A field name shared by the settings model and the save-input. */
export type SettingsField = keyof BusinessNotificationSettingsInput;

/** Per-channel on/off toggle field names for one event (only present channels). */
export interface ChannelToggles {
    email?: SettingsField;
    sms?: SettingsField;
    inApp?: SettingsField;
    push?: SettingsField;
}

export interface NotifEvent {
    /** Stable id, also the i18n/template key. */
    key: string;
    /** data-cy prefix — preserves the existing Cypress selectors. */
    cy: string;
    /** i18n key for the card title shown to the business owner. */
    labelKey: string;
    /** i18n key for the short helper line under the title (optional). */
    hintKey?: string;
    toggles: ChannelToggles;
    /** Custom e-mail body field (omit when the event has no e-mail copy). */
    emailText?: SettingsField;
    /** Custom SMS body field (omit when the event has no SMS copy). */
    smsText?: SettingsField;
    /** Category + subject passed to the branded e-mail preview endpoint. */
    preview: { category: string; title: string };
}

export interface NotifStage {
    key: string;
    /** i18n key for the stage label. */
    labelKey: string;
    /** i18n key for the blurb. */
    blurbKey: string;
    events: NotifEvent[];
}

/** Fallback body used by the e-mail preview when the owner hasn't typed text.
 *  Follows the business language (pass i18n.language). */
export function defaultEmailBody(key: string, lang?: string): string {
    return templatesFor(lang)[key]?.email?.[0]?.text ?? '';
}

/** Default SMS copy shown (placeholder + preview) when the owner hasn't typed text. */
export function defaultSmsBody(key: string, lang?: string): string {
    return templatesFor(lang)[key]?.sms?.[0]?.text ?? '';
}

const ALL: NotifEvent[] = [
    {
        key: 'new-booking',
        cy: 'new-booking',
        labelKey: 'settings.notification.events.newBooking',
        hintKey: 'settings.notification.events.newBookingHint',
        toggles: {
            email: 'sendNewBookingEmail',
            sms: 'sendNewBookingSMS',
            inApp: 'sendNewBookingInApp',
            push: 'sendNewBookingPush',
        },
        emailText: 'textForNewBookingEmail',
        smsText: 'textForNewBookingSMS',
        preview: { category: 'APPOINTMENT_BOOKED', title: 'Recebemos seu agendamento' },
    },
    {
        key: 'confirmation',
        cy: 'confirmation',
        labelKey: 'settings.notification.events.confirmation',
        hintKey: 'settings.notification.events.confirmationHint',
        toggles: {
            email: 'sendConfirmationEmail',
            sms: 'sendConfirmationSMS',
            inApp: 'sendConfirmationInApp',
            push: 'sendConfirmationPush',
        },
        emailText: 'textForConfirmationEmail',
        smsText: 'textForConfirmationSMS',
        preview: { category: 'APPOINTMENT_CONFIRMED', title: 'Agendamento confirmado' },
    },
    {
        key: 'reschedule',
        cy: 'reschedule',
        labelKey: 'settings.notification.events.reschedule',
        hintKey: 'settings.notification.events.rescheduleHint',
        toggles: {
            email: 'sendRescheduleBookingEmail',
            sms: 'sendRescheduleBookingSMS',
            inApp: 'sendRescheduleBookingInApp',
            push: 'sendRescheduleBookingPush',
        },
        emailText: 'textForRescheduleBookingEmail',
        smsText: 'textForRescheduleBookingSMS',
        preview: { category: 'APPOINTMENT_BOOKED', title: 'Agendamento remarcado' },
    },
    {
        key: 'reminder',
        cy: 'reminder',
        labelKey: 'settings.notification.events.reminder',
        hintKey: 'settings.notification.events.reminderHint',
        toggles: {
            email: 'sendAppointmentReminderEmail',
            sms: 'sendAppointmentReminderSMS',
            inApp: 'sendAppointmentReminderInApp',
            push: 'sendAppointmentReminderPush',
        },
        emailText: 'textForAppointmentReminderEmail',
        smsText: 'textForAppointmentReminderSMS',
        preview: { category: 'APPOINTMENT_BOOKED', title: 'Lembrete de agendamento' },
    },
    {
        key: 'rebook',
        cy: 'rebook',
        labelKey: 'settings.notification.events.rebook',
        hintKey: 'settings.notification.events.rebookHint',
        toggles: {
            email: 'sendAppointmentReebokReminderEmail',
            inApp: 'sendAppointmentReebokReminderInApp',
            push: 'sendAppointmentReebokReminderPush',
        },
        emailText: 'textForAppointmentRebookReminderEmail',
        preview: { category: 'APPOINTMENT_BOOKED', title: 'Que tal remarcar?' },
    },
    {
        key: 'cancellation',
        cy: 'cancellation',
        labelKey: 'settings.notification.events.cancellation',
        hintKey: 'settings.notification.events.cancellationHint',
        toggles: {
            email: 'sendCancellationBookingEmail',
            sms: 'sendCancellationBookingSMS',
            inApp: 'sendCancellationBookingInApp',
            push: 'sendCancellationBookingPush',
        },
        emailText: 'textForCancellationBookingEmail',
        smsText: 'textForCancellationBookingSMS',
        preview: { category: 'APPOINTMENT_CANCELLED', title: 'Agendamento cancelado' },
    },
    {
        key: 'decline',
        cy: 'decline',
        labelKey: 'settings.notification.events.decline',
        hintKey: 'settings.notification.events.declineHint',
        toggles: {
            email: 'sendDeclineBookingEmail',
            sms: 'sendDeclineBookingSMS',
            inApp: 'sendDeclineBookingInApp',
            push: 'sendDeclineBookingPush',
        },
        emailText: 'textForDeclineBookingEmail',
        smsText: 'textForDeclineBookingSMS',
        preview: { category: 'APPOINTMENT_DECLINED', title: 'Agendamento recusado' },
    },
    {
        key: 'waitlist-entry',
        cy: 'waitlist',
        labelKey: 'settings.notification.events.waitlistEntry',
        hintKey: 'settings.notification.events.waitlistEntryHint',
        toggles: {
            email: 'sendNewEntryWaitListEmail',
            sms: 'sendNewEntryWaitListSMS',
            inApp: 'sendNewEntryWaitListInApp',
            push: 'sendNewEntryWaitListPush',
        },
        emailText: 'textForNewEntryWaitListEmail',
        smsText: 'textForNewEntryWaitListSMS',
        preview: { category: 'APPOINTMENT_BOOKED', title: 'Voce esta na lista de espera' },
    },
    {
        key: 'waitlist-cancel',
        cy: 'waitlist-cancel',
        labelKey: 'settings.notification.events.waitlistCancel',
        hintKey: 'settings.notification.events.waitlistCancelHint',
        toggles: {
            email: 'sendCancellationWaitListEmail',
            sms: 'sendCancellationWaitListSMS',
            inApp: 'sendCancellationWaitListInApp',
            push: 'sendCancellationWaitListPush',
        },
        emailText: 'textForCancellationWaitListEmail',
        smsText: 'textForCancellationWaitListSMS',
        preview: { category: 'APPOINTMENT_CANCELLED', title: 'Saiu da lista de espera' },
    },
    {
        key: 'finish',
        cy: 'finish',
        labelKey: 'settings.notification.events.finish',
        hintKey: 'settings.notification.events.finishHint',
        toggles: {
            email: 'sendFinishAppointmentEmail',
            sms: 'sendFinishAppointmentSMS',
            inApp: 'sendFinishAppointmentInApp',
            push: 'sendFinishAppointmentPush',
        },
        emailText: 'textForFinishAppointmentEmail',
        smsText: 'textForFinishAppointmentSMS',
        preview: { category: 'APPOINTMENT_CONFIRMED', title: 'Atendimento concluido' },
    },
    {
        key: 'ask-review',
        cy: 'ask-review',
        labelKey: 'settings.notification.events.askReview',
        hintKey: 'settings.notification.events.askReviewHint',
        toggles: {
            email: 'askClientForReview',
        },
        emailText: 'textForReviewEmail',
        preview: { category: 'APPOINTMENT_CONFIRMED', title: 'Conte como foi seu atendimento' },
    },
    {
        key: 'birthday',
        cy: 'birthday',
        labelKey: 'settings.notification.events.birthday',
        hintKey: 'settings.notification.events.birthdayHint',
        toggles: {
            email: 'sendBirthdayEmail',
            sms: 'sendBirthdaySMS',
            inApp: 'sendBirthdayInApp',
            push: 'sendBirthdayPush',
        },
        emailText: 'textForBirthdayEmail',
        smsText: 'textForBirthdaySMS',
        preview: { category: 'PAYMENT_RECEIVED', title: 'Feliz aniversario!' },
    },
];

const byKey = (k: string): NotifEvent => ALL.find((e) => e.key === k)!;

/** Events grouped by appointment lifecycle stage (drives the sub-tabs). */
export const NOTIFICATION_STAGES: NotifStage[] = [
    {
        key: 'agendamento',
        labelKey: 'settings.notification.stages.agendamento',
        blurbKey: 'settings.notification.stages.agendamentoBlurb',
        events: ['new-booking', 'confirmation', 'reschedule'].map(byKey),
    },
    {
        key: 'lembretes',
        labelKey: 'settings.notification.stages.lembretes',
        blurbKey: 'settings.notification.stages.lembretesBlurb',
        events: ['reminder', 'rebook'].map(byKey),
    },
    {
        key: 'cancelamento',
        labelKey: 'settings.notification.stages.cancelamento',
        blurbKey: 'settings.notification.stages.cancelamentoBlurb',
        events: ['cancellation', 'decline'].map(byKey),
    },
    {
        key: 'espera',
        labelKey: 'settings.notification.stages.espera',
        blurbKey: 'settings.notification.stages.esperaBlurb',
        events: ['waitlist-entry', 'waitlist-cancel'].map(byKey),
    },
    {
        key: 'pos',
        labelKey: 'settings.notification.stages.pos',
        blurbKey: 'settings.notification.stages.posBlurb',
        events: ['finish', 'ask-review', 'birthday'].map(byKey),
    },
];
